import React, { useState, useEffect } from 'react'
import { StatisticsResponse } from '../types'
import { apiClient } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import './ProgressDashboard.css'

const ProgressDashboard: React.FC = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState<StatisticsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'session' | 'progress' | 'difficulty'>('overview')

  useEffect(() => {
    loadStatistics()
  }, [])

  const loadStatistics = async () => {
    setLoading(true)
    setError(null)

    try {
      if (!user || !user.user_id) {
        throw new Error('ユーザーが認証されていません')
      }

      const response = await apiClient.getUserStatistics()
      // Backend returns data directly
      if (response.data) {
        setStats(response.data)
      } else {
        setError('統計を読み込めませんでした')
      }
    } catch (err: any) {
      console.error('Error loading statistics:', err)
      setError(err.message || '統計を読み込めませんでした')
    } finally {
      setLoading(false)
    }
  }

  const formatRetention = (retention: number) => {
    return Math.round(retention)
  }

  const getRetentionColor = (retention: number) => {
    if (retention >= 80) return '#4caf50'
    if (retention >= 60) return '#8bc34a'
    if (retention >= 40) return '#ffc107'
    if (retention >= 20) return '#ff9800'
    return '#f44336'
  }

  const getRetentionLevel = (retention: number) => {
    if (retention >= 80) return '優秀'
    if (retention >= 60) return '良い'
    if (retention >= 40) return '普通'
    if (retention >= 20) return '平均以下'
    return '要練習'
  }

  const getProgressPercentage = (total: number, reviewed: number) => {
    return total > 0 ? Math.round((reviewed / total) * 100) : 0
  }

  if (loading) {
    return (
      <div className="progress-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>進捗を読み込み中...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="progress-dashboard">
        <div className="error-container">
          <h2>統計エラー</h2>
          <p>{error}</p>
          <button onClick={loadStatistics} className="retry-btn">
            再試行
          </button>
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="progress-dashboard">
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>統計データがありません</h3>
          <p>復習を始めて進捗を確認しましょう！</p>
        </div>
      </div>
    )
  }

  const retentionColor = getRetentionColor(stats.average_retention)
  const retentionLevel = getRetentionLevel(stats.average_retention)
  const progressPercentage = getProgressPercentage(stats.total_cards, stats.reviewed_today)

  return (
    <div className="progress-dashboard">
      <div className="dashboard-header">
        <h1>学習の進捗</h1>
        <p>日本語学習の旅と上達を時系列で追跡</p>
      </div>

      {/* Navigation Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            概要
          </button>
          <button
            className={`tab ${activeTab === 'session' ? 'active' : ''}`}
            onClick={() => setActiveTab('session')}
          >
            セッション統計
          </button>
          <button
            className={`tab ${activeTab === 'progress' ? 'active' : ''}`}
            onClick={() => setActiveTab('progress')}
          >
            進捗
          </button>
          <button
            className={`tab ${activeTab === 'difficulty' ? 'active' : ''}`}
            onClick={() => setActiveTab('difficulty')}
          >
            難易度別
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="overview-section">
          <div className="stats-grid">
            <div className="stat-card primary">
              <div className="stat-icon">📚</div>
              <div className="stat-info">
                <div className="stat-label">合計カード数</div>
                <div className="stat-value">{stats.total_cards}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⏰</div>
              <div className="stat-info">
                <div className="stat-label">今日の期限切れ</div>
                <div className="stat-value">{stats.due_cards}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🆕</div>
              <div className="stat-info">
                <div className="stat-label">新規カード</div>
                <div className="stat-value">{stats.new_cards}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-info">
                <div className="stat-label">今日復習した数</div>
                <div className="stat-value">{stats.reviewed_today}</div>
              </div>
            </div>
          </div>

          {/* Retention Rate */}
          <div className="retention-section">
            <div className="retention-card">
              <div className="retention-header">
                <h3>定着率</h3>
                <span className="retention-level" style={{ color: retentionColor }}>
                  {retentionLevel}
                </span>
              </div>
              <div className="retention-display">
                <div className="retention-circle">
                  <svg viewBox="0 0 36 36" className="retention-svg">
                    <path
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e0e0e0"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={retentionColor}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray={`${stats.average_retention}, 100`}
                    />
                  </svg>
                  <div className="retention-text">
                    <div className="retention-number">{formatRetention(stats.average_retention)}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Streak */}
          {stats.streak_days > 0 && (
            <div className="streak-section">
              <div className="streak-card">
                <div className="streak-icon">🔥</div>
                <div className="streak-info">
                  <div className="streak-label">連続学習</div>
                  <div className="streak-value">{stats.streak_days} 日</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Session Stats Tab */}
      {activeTab === 'session' && (
        <div className="session-section">
          <div className="section-header">
            <h2>セッション統計</h2>
            <p>毎日の復習パフォーマンスを追跡</p>
          </div>
          <div className="session-stats-grid">
            <div className="session-stat-card">
              <div className="session-stat-icon">🎯</div>
              <div className="session-stat-info">
                <div className="session-stat-label">復習したカード</div>
                <div className="session-stat-value">{stats.reviewed_today}</div>
                <div className="session-stat-sub">今日</div>
              </div>
            </div>
            <div className="session-stat-card">
              <div className="session-stat-icon">⏱️</div>
              <div className="session-stat-info">
                <div className="session-stat-label">期限切れカード</div>
                <div className="session-stat-value">{stats.due_cards}</div>
                <div className="session-stat-sub">復習待ち</div>
              </div>
            </div>
            <div className="session-stat-card">
              <div className="session-stat-icon">📈</div>
              <div className="session-stat-info">
                <div className="session-stat-label">新規カード</div>
                <div className="session-stat-value">{stats.new_cards}</div>
                <div className="session-stat-sub">学習準備完了</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-bar-section">
            <div className="progress-header">
              <span>今日の進捗</span>
              <span>{progressPercentage}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${progressPercentage}%`,
                  background: progressPercentage >= 50 ? '#4caf50' : '#ffc107'
                }}
              ></div>
            </div>
            <div className="progress-subtext">
              今日は {stats.total_cards} 枚中 {stats.reviewed_today} 枚復習済み
            </div>
          </div>
        </div>
      )}

      {/* Progress Tab */}
      {activeTab === 'progress' && (
        <div className="progress-section">
          <div className="section-header">
            <h2>学習の進捗</h2>
            <p>日本語学習の旅</p>
          </div>
          <div className="progress-metrics">
            <div className="metric-card">
              <div className="metric-icon">📊</div>
              <div className="metric-info">
                <div className="metric-label">総語彙数</div>
                <div className="metric-value">{stats.total_cards}</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">🔄</div>
              <div className="metric-info">
                <div className="metric-label">アクティブな復習</div>
                <div className="metric-value">{stats.due_cards}</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">🧠</div>
              <div className="metric-info">
                <div className="metric-label">記憶の強度</div>
                <div className="metric-value" style={{ color: retentionColor }}>
                  {formatRetention(stats.average_retention)}%
                </div>
              </div>
            </div>
          </div>

          {/* Last Review */}
          {stats.last_review_date && (
            <div className="last-review-section">
              <div className="last-review-card">
                <div className="last-review-icon">📅</div>
                <div className="last-review-info">
                  <div className="last-review-label">最終復習日</div>
                  <div className="last-review-date">
                    {new Date(stats.last_review_date).toLocaleDateString('ja-JP', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Difficulty Tab */}
      {activeTab === 'difficulty' && (
        <div className="difficulty-section">
          <div className="section-header">
            <h2>難易度別パフォーマンス</h2>
            <p>各JLPTレベルでの成績</p>
          </div>
          <div className="difficulty-grid">
            <div className="difficulty-card">
              <div className="difficulty-header">
                <span className="difficulty-level" style={{ backgroundColor: '#4caf50' }}>N5</span>
                <span className="difficulty-name">初級</span>
              </div>
              <div className="difficulty-stats">
                <div className="difficulty-stat">
                  <span className="stat-label">カード</span>
                  <span className="stat-value">0</span>
                </div>
                <div className="difficulty-stat">
                  <span className="stat-label">定着率</span>
                  <span className="stat-value">0%</span>
                </div>
              </div>
            </div>

            <div className="difficulty-card">
              <div className="difficulty-header">
                <span className="difficulty-level" style={{ backgroundColor: '#8bc34a' }}>N4</span>
                <span className="difficulty-name">初中級</span>
              </div>
              <div className="difficulty-stats">
                <div className="difficulty-stat">
                  <span className="stat-label">カード</span>
                  <span className="stat-value">0</span>
                </div>
                <div className="difficulty-stat">
                  <span className="stat-label">定着率</span>
                  <span className="stat-value">0%</span>
                </div>
              </div>
            </div>

            <div className="difficulty-card">
              <div className="difficulty-header">
                <span className="difficulty-level" style={{ backgroundColor: '#ffc107' }}>N3</span>
                <span className="difficulty-name">中級</span>
              </div>
              <div className="difficulty-stats">
                <div className="difficulty-stat">
                  <span className="stat-label">カード</span>
                  <span className="stat-value">0</span>
                </div>
                <div className="difficulty-stat">
                  <span className="stat-label">定着率</span>
                  <span className="stat-value">0%</span>
                </div>
              </div>
            </div>

            <div className="difficulty-card">
              <div className="difficulty-header">
                <span className="difficulty-level" style={{ backgroundColor: '#ff9800' }}>N2</span>
                <span className="difficulty-name">上級</span>
              </div>
              <div className="difficulty-stats">
                <div className="difficulty-stat">
                  <span className="stat-label">カード</span>
                  <span className="stat-value">0</span>
                </div>
                <div className="difficulty-stat">
                  <span className="stat-label">定着率</span>
                  <span className="stat-value">0%</span>
                </div>
              </div>
            </div>

            <div className="difficulty-card">
              <div className="difficulty-header">
                <span className="difficulty-level" style={{ backgroundColor: '#f44336' }}>N1</span>
                <span className="difficulty-name">超上級</span>
              </div>
              <div className="difficulty-stats">
                <div className="difficulty-stat">
                  <span className="stat-label">カード</span>
                  <span className="stat-value">0</span>
                </div>
                <div className="difficulty-stat">
                  <span className="stat-label">定着率</span>
                  <span className="stat-value">0%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProgressDashboard